import React from 'react';
import { User, ShieldCheck, Bookmark, Coins, Dices } from 'lucide-react';

/**
 * Component for displaying the character sheet with generated data
 */
const CharacterSheet = ({ stats, characterClass, characterRace, characterBackground, equipment, gold, statMode }) => {
  if (!stats || !characterClass || !characterRace) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 p-6">
        <h2 className="text-2xl font-bold text-white">
          {characterRace.subrace ? `${characterRace.subrace} ` : ''}{characterRace.race} {characterClass.class}
          {characterClass.subclass ? ` (${characterClass.subclass})` : ''}
        </h2>
        <p className="text-gray-200">{characterBackground} Background</p>
      </div>

      <div className="p-6">
        {/* Stats Section */}
        <div className="mb-6">
          <h3 className="text-lg font-bold mb-3 flex items-center">
            <User className="mr-2" /> Ability Scores
          </h3>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {Object.entries(stats).map(([ability, details]) => (
              <div key={ability} className="bg-gray-100 p-4 rounded-lg text-center">
                <h4 className="font-bold">{ability}</h4>
                <p className="text-2xl font-bold">{details.score}</p>
                <p className="text-sm">
                  {details.modifier >= 0 ? `+${details.modifier}` : details.modifier}
                </p>

                {/* Dice rolls visualization */}
                <div className="flex justify-center mt-2 flex-wrap gap-1">
                  {details.rolls && details.rolls.map((roll, idx) => {
                    if (statMode === "hardcore" && idx >= 3) {
                      return null;
                    }

                    const totalDice = details.rolls.length;
                    const isLastOfFour = totalDice === 4 && idx === 3;
                    const bgColor = isLastOfFour ? 'bg-red-500 text-white' : 'bg-green-500 text-white';

                    return (
                      <span 
                        key={idx}
                        className={`inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full ${bgColor}`}
                      >
                        {roll}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Race, Class, Background */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-bold mb-2 flex items-center">
              <User className="mr-2" /> Race
            </h3>
            <p className="bg-gray-100 p-3 rounded">
              {characterRace.race}
              {characterRace.subrace && (
                <span className="block text-sm text-gray-600">Subrace: {characterRace.subrace}</span>
              )}
            </p>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-2 flex items-center">
              <ShieldCheck className="mr-2" /> Class
            </h3>
            <p className="bg-gray-100 p-3 rounded">
              {characterClass.class}
              {characterClass.subclass && (
                <span className="block text-sm text-gray-600">Subclass: {characterClass.subclass}</span>
              )}
            </p>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-2 flex items-center">
              <Bookmark className="mr-2" /> Background
            </h3>
            <p className="bg-gray-100 p-3 rounded">{characterBackground}</p>
          </div>
        </div>

        {/* Equipment and Gold */}
        <div className="grid grid-cols-1">
          <div>
            <h3 className="text-lg font-bold mb-2 flex items-center">
              <Coins className="mr-2" /> Starting Gold
            </h3>
            <div className="bg-gray-100 p-3 rounded">
              <p className="text-xl">{typeof gold === 'object' ? gold.total : gold} gp</p>

              {/* Gold dice rolls */}
              {gold && typeof gold === 'object' && gold.diceRolls && (
                <div className="mt-2">
                  <div className="flex items-center">
                    <Dices className="w-4 h-4 mr-1" />
                    <span className="text-sm">
                      Rolled {gold.diceCount || gold.diceRolls.length}d6 × {gold.multiplier || 10}:
                    </span>
                  </div>
                  <div className="flex mt-1 flex-wrap gap-1">
                    {gold.diceRolls.map((roll, idx) => {
                      if (statMode === "hardcore" && idx >= 3) {
                        return null;
                      }
                      return (
                        <span 
                          key={idx}
                          className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full bg-green-500 text-white"
                        >
                          {roll}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterSheet;
